// LegalCompliancePage.js
import React from 'react';
import { Box, Typography, Container, Breadcrumbs, Link as MuiLink, List, ListItem, ListItemText } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const LegalCompliancePage = () => {
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Legal & Company', path: '/legal' },
    { name: 'Legal & Compliance', path: '/legal' },
  ];

  const complianceAreas = [
    {
      title: 'Regulatory Adherence',
      description: 'We strictly adhere to all relevant national and international laws and regulations governing the trade and recycling of scrap materials. Our operations are designed to meet the highest standards of legal compliance.',
      details: [
        'Compliance with environmental protection laws.',
        'Adherence to international trade agreements.',
        'Regular audits and legal reviews.',
      ],
    },
    {
      title: 'Data Protection & Privacy',
      description: 'Protecting your data is paramount. We implement robust data security measures and comply with global data privacy regulations to ensure your personal and business information is safe.',
      details: [
        'GDPR and CCPA compliance.',
        'Secure data storage and transmission protocols.',
        'Transparent privacy policies.',
      ],
    },
    {
      title: 'Anti-Theft & Verification',
      description: 'Our platform incorporates stringent measures to prevent the trade of stolen materials and ensure the legitimacy of all transactions. We work closely with law enforcement agencies where necessary.',
      details: [
        'Comprehensive seller and material verification processes.',
        'Collaboration with industry databases for stolen property.',
        'Reporting mechanisms for suspicious activities.',
      ],
    },
    {
      title: 'Ethical Business Conduct',
      description: 'We are committed to conducting business with the highest ethical standards, promoting fair competition, preventing corruption, and ensuring transparency in all our dealings.',
      details: [
        'Strict anti-bribery and corruption policies.',
        'Code of conduct for all employees and partners.',
        'Whistleblower protection mechanisms.',
      ],
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 2 }}>
      <Container sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: { xs: 2, md: 4 }, px: { xs: 2, md: 0 } }}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            {breadcrumbs.map((crumb, index) => (
              crumb.path ? (
                <MuiLink component={RouterLink} to={crumb.path} key={index} underline="hover" color="text.secondary" variant="body2">
                  {crumb.name}
                </MuiLink>
              ) : (
                <Typography key={index} color="text.primary" variant="body2">{crumb.name}</Typography>
              )
            ))}
          </Breadcrumbs>
        </Box>

        <Typography variant="h3" align="center" fontWeight={700} sx={{ mb: { xs: 4, md: 6 }, color: 'text.primary' }}>
          Legal & Compliance
        </Typography>

        <Typography variant="body1" align="center" sx={{ mb: { xs: 4, md: 6 }, maxWidth: '800px', mx: 'auto', color: 'text.secondary' }}>
          Our commitment to integrity and responsibility is reflected in our robust legal and compliance framework. We operate with full transparency and adherence to global standards.
        </Typography>

        <Box sx={{ mt: 6 }}>
          {complianceAreas.map((area, index) => (
            <Box
              key={index}
              sx={{
                mb: 6,
                p: { xs: 3, md: 5 },
                borderRadius: 4,
                background: 'background.paper',
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                border: '1px solid',
                borderColor: 'grey.200',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 16px 32px rgba(0,0,0,0.12)',
                },
              }}
            >
              <Typography variant="h4" fontWeight={700} sx={{ mb: 2, color: 'primary.dark' }}>
                {area.title}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.primary' }}>
                {area.description}
              </Typography>

              <List dense disablePadding>
                {area.details.map((detail, detailIndex) => (
                  <ListItem key={detailIndex} disablePadding sx={{ py: 0.5 }}>
                    <ListItemText primary={detail} primaryTypographyProps={{ variant: 'body1', color: 'text.secondary' }} />
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default LegalCompliancePage;
